import { use } from 'react';
import { today, getLocalTimeZone } from '@internationalized/date';
import { useModelIndex, useModelShow } from '@rhino-project/core/hooks';
import type { RhinoOrder } from '@rhino-project/core';
import TeamContext from '../contexts/TeamContext';

export interface SeasonWindow {
  gteq: string; // active season's starts_on (inclusive lower bound)
  lt?: string; // next season's starts_on (exclusive upper bound); omitted if active is latest
}

const todayIso = () => today(getLocalTimeZone()).toString();

export const useCurrentSeason = () => {
  const { resources: seasons } = useModelIndex('season', {
    filter: { starts_on: { lteq: todayIso() } },
    order: ['-starts_on'],
    limit: 1
  });

  if (seasons?.results && seasons.results.length > 0) {
    return seasons.results[0];
  }

  return null;
};

export const useCurrentTeam = () => {
  const { team } = use(TeamContext);

  return team;
};

type TeamSeasonStatus = 'planning' | 'active' | 'archived';

// Historical lists read most-recent-first; any list that includes future
// (planning) seasons reads in calendar order, so a season picker runs the way a
// coach reads a schedule.
const NEWEST_FIRST: RhinoOrder<'team_season'> = ['-season.starts_on'];
const CHRONOLOGICAL: RhinoOrder<'team_season'> = ['season.starts_on'];

const useTeamSeasonsBy = (
  status: TeamSeasonStatus | TeamSeasonStatus[] | undefined,
  order: RhinoOrder<'team_season'>
) => {
  const team = useCurrentTeam();

  return useModelIndex('team_season', {
    filter: {
      team_id: team?.id,
      ...(status ? { status } : {})
    },
    order,
    queryOptions: { enabled: !!team }
  });
};

export const useTeamSeasons = () => useTeamSeasonsBy(undefined, NEWEST_FIRST);

export const useActiveSeasons = () => useTeamSeasonsBy('active', NEWEST_FIRST);

export const usePlanningSeasons = () =>
  useTeamSeasonsBy('planning', CHRONOLOGICAL);

export const useNonArchivedSeasons = () =>
  useTeamSeasonsBy(['active', 'planning'], CHRONOLOGICAL);

/**
 * The date window [active season starts_on, next season starts_on) for the given
 * active team_season, used to scope created_at/reported_at filters. Open-ended
 * (no `lt`) when the active season is the most recent one.
 */
export const useActiveSeasonWindow = (
  teamSeasonId?: string | number
): { window: SeasonWindow | undefined; isLoading: boolean } => {
  const { resource: teamSeason, isLoading: isTeamSeasonLoading } = useModelShow(
    'team_season',
    teamSeasonId as string | number,
    { queryOptions: { enabled: !!teamSeasonId } }
  );

  const season = (
    teamSeason as {
      season?: { starts_on?: string; sport_id?: number };
    } | null
  )?.season;
  const startsOn = season?.starts_on;
  const sportId = season?.sport_id;

  const { resources: nextSeasons, isLoading: isNextSeasonLoading } =
    useModelIndex('season', {
      filter: {
        ...(sportId ? { sport_id: sportId } : {}),
        starts_on: { gt: startsOn }
      },
      order: ['starts_on'],
      limit: 1,
      queryOptions: { enabled: !!startsOn }
    });

  const nextStartsOn = (
    nextSeasons?.results?.[0] as { starts_on?: string } | undefined
  )?.starts_on;

  const seasonWindow: SeasonWindow | undefined = startsOn
    ? { gteq: startsOn, ...(nextStartsOn ? { lt: nextStartsOn } : {}) }
    : undefined;

  return {
    window: seasonWindow,
    isLoading: isTeamSeasonLoading || (!!startsOn && isNextSeasonLoading)
  };
};
